import React, { ReactNode } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import CircleLoader, { Types } from '@lib/components/CircleLoader/CircleLoader';
import { SelectOption, SelectPanelOptionRendererProps } from '../types';
import { getComparableKey } from '../utils';
import { SelectPanelOption } from './SelectPanelOption';
import styles from '../SelectPanel.module.scss';

type SelectPanelOptionsListProps = {
  debouncedSearch: string;
  filteredOptions: SelectOption[];
  isLoading: boolean;
  isMulti: boolean;
  isOptionDisabled?: (option: SelectOption) => boolean;
  listAriaLabel?: string;
  listboxId: string;
  maxListHeight: number;
  onOptionClick: (option: SelectOption) => void;
  onScroll: (event: React.UIEvent<HTMLUListElement>) => void;
  renderOption?: (props: SelectPanelOptionRendererProps) => ReactNode;
  scrollable: boolean;
  selectedMap: Map<string, SelectOption>;
};

export function SelectPanelOptionsList({
  debouncedSearch,
  filteredOptions,
  isLoading,
  isMulti,
  isOptionDisabled,
  listAriaLabel,
  listboxId,
  maxListHeight,
  onOptionClick,
  onScroll,
  renderOption,
  scrollable,
  selectedMap,
}: SelectPanelOptionsListProps) {
  const { t } = useTranslation();

  const listClassName = classNames(styles.list, {
    [styles.listScrollable]: scrollable,
  });

  return (
    <ul
      id={listboxId}
      role="listbox"
      aria-multiselectable={isMulti}
      aria-label={
        listAriaLabel ?? t('select-option', { defaultValue: 'Select option' })
      }
      className={listClassName}
      onScroll={onScroll}
      style={scrollable ? { maxHeight: maxListHeight } : undefined}
    >
      {filteredOptions.map((option, index) => {
        const key = getComparableKey(option, index);
        const isSelected = selectedMap.has(key);
        const disabledOption = isOptionDisabled
          ? isOptionDisabled(option)
          : false;

        return (
          <SelectPanelOption
            key={key}
            debouncedSearch={debouncedSearch}
            disabled={disabledOption}
            index={index}
            isMulti={isMulti}
            isSelected={isSelected}
            listboxId={listboxId}
            onClick={() => onOptionClick(option)}
            option={option}
            renderOption={renderOption}
          />
        );
      })}
      {isLoading && (
        <div className={styles.loader}>
          <CircleLoader size={24} padding="0" type={Types.GIF} />
        </div>
      )}
    </ul>
  );
}
